import React from "react";
import { TimeSlot } from "../../../types";
import { StepIndicatorHeader } from "../../shared";

interface TimeSlotGridProps {
  timeSlots: TimeSlot[];
  selectedTimeSlot: TimeSlot | null;
  onTimeSlotSelect: (timeSlot: TimeSlot) => void;
  loading: boolean;
}

export const TimeSlotGrid: React.FC<TimeSlotGridProps> = ({
  timeSlots,
  selectedTimeSlot,
  onTimeSlotSelect,
  loading,
}) => {
  if (loading) {
    return (
      <div className="space-y-6 animate-fade-in">
        <StepIndicatorHeader
          stepNumber={4}
          title="Horários Disponíveis"
          description="Escolha o melhor horário para você"
        />

        <div className="flex flex-col items-center justify-center py-12 sm:py-16 space-y-4 sm:space-y-6">
          <div className="relative">
            <div className="animate-spin w-10 h-10 sm:w-12 sm:h-12 border-4 border-sky-200 border-t-sky-600 rounded-full"></div>
            <div className="absolute inset-0 animate-ping w-10 h-10 sm:w-12 sm:h-12 border-4 border-sky-300 rounded-full opacity-20"></div>
          </div>
          <p className="text-gray-600 font-medium text-base sm:text-lg">
            Carregando horários disponíveis...
          </p>
        </div>
      </div>
    );
  }

  if (timeSlots.length === 0) {
    return (
      <div className="space-y-6 animate-fade-in">
        <StepIndicatorHeader
          stepNumber={4}
          title="Horários Disponíveis"
          description="Escolha o melhor horário para você"
        />

        <div className="text-center py-12 sm:py-16">
          <div className="w-20 h-20 sm:w-24 sm:h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
            <span className="text-3xl sm:text-4xl text-gray-400">⏰</span>
          </div>
          <h4 className="text-lg sm:text-xl font-semibold text-gray-800 mb-2">
            Nenhum horário disponível
          </h4>
          <p className="text-sm sm:text-base text-gray-600 max-w-md mx-auto px-4">
            Não há horários disponíveis para a data selecionada. Tente escolher
            outra data.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 animate-slide-up">
      <StepIndicatorHeader
        stepNumber={4}
        title="Horários Disponíveis"
        description="Escolha o melhor horário para você"
      />

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3 sm:gap-4 max-w-4xl mx-auto">
        {timeSlots.map((slot, index) => (
          <button
            key={slot.startTime}
            onClick={() => onTimeSlotSelect(slot)}
            className={`group relative overflow-hidden p-3 sm:p-4 rounded-xl border-2 transition-all duration-300 transform hover:scale-105 ${
              selectedTimeSlot?.startTime === slot.startTime
                ? "bg-gradient-to-br from-sky-500 to-sky-600 border-sky-400 text-white shadow-large ring-4 ring-sky-200"
                : "bg-white border-gray-200 hover:border-sky-300 text-gray-700 shadow-soft hover:shadow-medium"
            }`}
            style={{ animationDelay: `${index * 50}ms` }}
            aria-label={`${slot.startTime} - Disponível`}
          >
            <div className="relative z-10 text-center space-y-1">
              <div
                className={`text-base sm:text-lg font-bold ${
                  selectedTimeSlot?.startTime === slot.startTime
                    ? "text-white"
                    : "text-gray-800"
                }`}
              >
                {slot.startTime}
              </div>
              <div
                className={`text-xs font-medium flex items-center justify-center gap-1 ${
                  selectedTimeSlot?.startTime === slot.startTime
                    ? "text-white/90"
                    : "text-gray-500"
                }`}
              >
                <span>⏱️</span>
                <span>{slot.duration} min</span>
              </div>
            </div>

            {/* Hover effect background */}
            <div
              className={`absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 ${
                selectedTimeSlot?.startTime === slot.startTime
                  ? "bg-white/10"
                  : "bg-gradient-to-br from-sky-50 to-sky-100"
              }`}
            ></div>

            {/* Selection indicator */}
            {selectedTimeSlot?.startTime === slot.startTime && (
              <div className="absolute top-2 right-2 w-4 h-4 bg-white rounded-full flex items-center justify-center">
                <div className="w-2 h-2 bg-sky-600 rounded-full"></div>
              </div>
            )}
          </button>
        ))}
      </div>

      <div className="text-center">
        <p className="text-xs sm:text-sm text-gray-500">
          ✨ Todos os horários são baseados no fuso horário de Brasília
        </p>
      </div>
    </div>
  );
};
